import type { PayloadAction } from '@reduxjs/toolkit';
import keyBy from 'lodash/keyBy';
import type { ListObj } from 'src/helper/shared-types';
import { doAgendaListSorting } from 'src/helper/work-order-utils';
import type { CalendarObj, InitialStateType } from 'src/redux/user-management/types';

const reducers = {
  updateBlockOutTimeSource: (
    state: InitialStateType,
    action: PayloadAction<{
      response: CalendarObj[];
      list: Array<ListObj>;
      userId: string;
    }>,
  ) => {
    const { response = [], list = [], userId } = action.payload;
    state.calendarSource = keyBy(response, 'technician_appointment_id');
    const newSource = { ...(state.source || {}) };
    if (userId) {
      const newData = { ...(newSource[userId] || {}) };
      newData.agenda = { ...(newData?.agenda || {}), list };
      newSource[userId] = newData;
      state.source = newSource;
    }
  },

  modifyBlockOutTime: (
    state: InitialStateType,
    action: PayloadAction<{
      response: CalendarObj;
      userId?: string;
    }>,
  ) => {
    const { response, userId } = action.payload;

    if (response?.technician_appointment_id) {
      state.calendarSource = {
        ...(state.calendarSource || {}),
        [response?.technician_appointment_id]: response || {},
      };

      if (userId) {
        const newSource = { ...(state.source || {}) };
        const newData = { ...(newSource?.[userId] || {}) };

        const isNewId = !(newData?.agenda?.list || [])?.some(
          (item) => item?.technicianAppointmentId === response?.technician_appointment_id,
        );

        const newList = [...(newData?.agenda?.list || [])];
        if (isNewId) {
          newList.push({
            technicianAppointmentId: response?.technician_appointment_id,
            type: 'block',
          });
        }
        const sortedAgendaList = doAgendaListSorting(newList, state?.calendarSource);
        newData.agenda = {
          ...(newData?.agenda || {}),
          list: sortedAgendaList,
        };
        state.source = { ...newSource, [userId]: newData };
      }
    }
  },

  removeItemFromAgendaList: (
    state: InitialStateType,
    action: PayloadAction<{
      // itemId can be wo-id or technician_appointment_id
      // wo-id to remove WO and technician_appointment_id to remove block-out
      itemId: string;
      userId?: string;
    }>,
  ) => {
    const { itemId, userId } = action.payload;
    if (itemId && userId) {
      const newSource = { ...(state.source || {}) };
      const newData = { ...(newSource[userId] || {}) };
      newData.agenda = {
        ...(newData?.agenda || {}),
        list: (newData?.agenda?.list || [])?.filter(
          (item) => ![item?.woId, item?.technicianAppointmentId]?.includes(itemId),
        ),
      };
      newSource[userId] = newData;
      state.source = newSource;
    }
  },

  updateTechnicianTimezone: (
    state: InitialStateType,
    action: PayloadAction<{ userId: string; timezone: string }>,
  ) => {
    const { userId, timezone } = action.payload;
    const newSource = { ...(state.source || {}) };
    if (userId && timezone) {
      newSource[userId] = {
        ...(newSource[userId] || {}),
        account_info: {
          ...(newSource[userId]?.account_info || {}),
          user_id: userId,
        },
        timezone,
      };
      state.source = newSource;
    }
  },
};

export default reducers;
